import { AppHeader, AppSidebar, PlaneSwitcherMenu, UserMenu } from '@today/ui-components'
import { PlaneShellFrame } from '@today/ui-domain'
import type { ReactElement } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useAuth } from '@/hooks/useAuth'
import { PLATFORM_NAV_GROUPS, platformNavPredicates } from '../platform-nav'
import { useShellSession } from '../session/ShellSessionProvider'
import { PlaneId, planeBasePath, usePlaneTargets } from '../usePlaneTargets'

/** Account-menu actions; the ids are reported by `UserMenu.onAction`. */
const ACCOUNT_ENTRIES = [
	{ id: 'settings', label: 'Settings' },
	{ id: 'logout', label: 'Logout' },
]

/** The deploy stage shown in the footer (public Vite config; `'local'` when unset). */
const STAGE = (import.meta.env.VITE_APP_STAGE as string | undefined) ?? 'local'

/** The platform sidebar: the nav config + predicates bound to the viewer's atoms. */
function PlatformSidebar({ permissions, currentPath }: { permissions: readonly string[]; currentPath: string }) {
	return (
		<AppSidebar
			groups={PLATFORM_NAV_GROUPS}
			predicates={platformNavPredicates}
			permissions={permissions}
			currentPath={currentPath}
			label="Platform navigation"
			renderLink={(_item, props) => (
				<NavLink to={props.target} aria-current={props['aria-current']} className={props.className}>
					{props.children}
				</NavLink>
			)}
		/>
	)
}

/** The deploy-stage footer (no region value is wired in the app's public config yet). */
function PlatformFooter() {
	return (
		<footer className="mt-ds-6 border-t border-ds-border px-ds-gutter py-ds-3 text-ds-xs text-ds-text-subtle">
			Platform console · {STAGE}
		</footer>
	)
}

/**
 * Platform Console plane shell (AD-4 / W1-2).
 *
 * A thin data+policy binder over the shared TO-633 substrate: it feeds the
 * platform nav config + the viewer's permission atoms to `AppSidebar`, the
 * `today` plane title + a `PlaneSwitcherMenu` + a `UserMenu` to `AppHeader`, and
 * the routed `<Outlet/>` + a `stage` footer to `PlaneShellFrame`. It reimplements
 * no presentation — the frame, sidebar, header and menus are the shared
 * components. Mounted at `/admin`, replacing the shared `Layout` for the platform
 * plane only (`frontend-architecture.md` §4).
 *
 * The context band (W1-6 / TO-638) comes from {@link useShellSession}: the shell
 * forwards its `band` node into the frame's band slot. Platform is the widest plane,
 * so the step band derives `null` here — it lights up once a narrower plane also
 * mounts `PlaneShellFrame` (Wave-2). `enter`/`actas` are blocked on AD-5.
 */
export function PlatformShell(): ReactElement {
	const { user, logout } = useAuth()
	const { permissions } = useViewerPermissions()
	const { targets, tenantSlug } = usePlaneTargets()
	const { band } = useShellSession()
	const location = useLocation()
	const navigate = useNavigate()

	function handleAccountAction(id: string): void {
		if (id === 'logout') {
			logout()
			return
		}
		if (id === 'settings') void navigate('/settings')
	}

	function handlePlaneSwitch(planeId: string): void {
		void navigate(planeBasePath(planeId as PlaneId, tenantSlug))
	}

	// Hide the switcher when the platform plane is the only plane the viewer can
	// reach: a one-entry switcher offers no navigation (REQ-004).
	const planeSwitcher =
		targets.length > 1 ? (
			<PlaneSwitcherMenu
				targets={targets}
				permissions={permissions}
				current={PlaneId.Platform}
				onSwitch={handlePlaneSwitch}
				label="Platform Console"
			/>
		) : undefined

	const accountLabel = user?.displayName || user?.email || 'Account'
	const userMenu = <UserMenu label={accountLabel} entries={ACCOUNT_ENTRIES} onAction={handleAccountAction} />

	return (
		<PlaneShellFrame
			sidebar={<PlatformSidebar permissions={permissions} currentPath={location.pathname} />}
			header={<AppHeader title="today" planeSwitcher={planeSwitcher} userMenu={userMenu} />}
			band={band}
		>
			<Outlet />
			<PlatformFooter />
		</PlaneShellFrame>
	)
}
